import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Card,
  Divider,
} from "@mui/material";
import { FaPlus, FaCheckCircle } from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import api from "../../api";

export default function Subscriptions() {
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [payData, setPayData] = useState({
    subscription_id: "",
    school_id: "",
    amount: "",
    payment_mode: "UPI",
    utr_number: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loggedInRole = localStorage.getItem("role");
  const fixedSchoolId = localStorage.getItem("school_id");

  // Fetch subscriptions
  const fetchSubs = async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const res = await api.get(`/super_admin/subscriptions/list?school_id=${sid}`);
      setSubs(res.data?.data || []);
    } catch (err) {
      console.error("Fetch subs error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch schools
  const fetchSchools = async () => {
    if (loggedInRole === "super_admin") {
      try {
        const res = await api.get("/super_admin/school/list");
        setSchools(res.data?.data || []);
      } catch (err) {
        console.error("Fetch schools error:", err);
      }
    }
  };

  useEffect(() => {
    fetchSchools();
    if (loggedInRole === "admin" && fixedSchoolId) {
      setSchoolId(fixedSchoolId);
      fetchSubs(fixedSchoolId);
    }
  }, []);

  useEffect(() => {
    if (schoolId) fetchSubs(schoolId);
  }, [schoolId]);

  const handleGenerate = async () => {
    try {
      const res = await api.post("/super_admin/subscriptions/generate_monthly", {});
      setSnackbar({ open: true, message: res.data.message, severity: res.data.status ? "success" : "error" });
      fetchSubs(schoolId);
    } catch {
      setSnackbar({ open: true, message: "Failed to generate monthly", severity: "error" });
    }
  };

  const openPay = (sub) => {
    setPayData({
      subscription_id: sub.id,
      school_id: schoolId,
      amount: sub.amount,
      payment_mode: "UPI",
      utr_number: "",
    });
    setOpenPayDialog(true);
  };

  const handlePaySave = async () => {
    try {
      const res = await api.post("/super_admin/subscriptions/pay", payData, {
        headers: { "Content-Type": "application/json" },
      });
      setSnackbar({ open: true, message: res.data.message, severity: res.data.status ? "success" : "error" });
      setOpenPayDialog(false);
      fetchSubs(schoolId);
    } catch {
      setSnackbar({ open: true, message: "Payment failed", severity: "error" });
    }
  };

  return (
    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MdSubscriptions size={28} color="#1976d2" />
          <Typography variant="h5" fontWeight={800}>
            School Subscriptions
          </Typography>
        </Box>
        {loggedInRole === "super_admin" && (
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            sx={{
              background: "linear-gradient(45deg,#1976d2,#42a5f5)",
              borderRadius: 2,
              textTransform: "none",
            }}
            onClick={handleGenerate}
          >
            Generate Monthly
          </Button>
        )}
      </Box>

      {/* School Select */}
      {loggedInRole === "super_admin" && (
        <Box sx={{ mb: 3 }}>
          <TextField
            select
            label="Select School"
            value={schoolId}
            onChange={(e) => setSchoolId(e.target.value)}
            sx={{ minWidth: 300 }}
            size="small"
          >
            {schools.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {/* Content Section */}
      {!schoolId ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            🎓 Please select a school to view subscriptions
          </Typography>
        </Box>
      ) : loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Fetching subscriptions...
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Month</b></TableCell>
                <TableCell><b>Amount</b></TableCell>
                <TableCell><b>Due Date</b></TableCell>
                <TableCell><b>Paid Date</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Mode</b></TableCell>
                <TableCell><b>Txn ID</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.month}</TableCell>
                  <TableCell>₹ {s.amount}</TableCell>
                  <TableCell>{s.due_date}</TableCell>
                  <TableCell>{s.paid_date || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.status.toUpperCase()}
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        background:
                          s.status === "paid"
                            ? "linear-gradient(45deg,#2e7d32,#66bb6a)"
                            : s.status === "pending"
                            ? "linear-gradient(45deg,#f57c00,#ffb74d)"
                            : "linear-gradient(45deg,#c62828,#ef5350)",
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{s.payment_mode || "-"}</TableCell>
                  <TableCell>{s.transaction_id || "-"}</TableCell>
                  <TableCell align="right">
                    {s.status !== "paid" && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openPay(s)}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Pay Now
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!subs.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="h6" color="text.secondary">
                      No subscriptions found 🚫
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}


      {/* Payment Dialog */}
      <Dialog open={openPayDialog} onClose={() => setOpenPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>💳 Pay Subscription</DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            margin="dense"
            label="Amount"
            fullWidth
            value={payData.amount}
            InputProps={{ readOnly: true }}
          />
          <TextField
            margin="dense"
            select
            label="Payment Mode"
            fullWidth
            value={payData.payment_mode}
            onChange={(e) => setPayData((f) => ({ ...f, payment_mode: e.target.value }))}
          >
            <MenuItem value="UPI">UPI</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Online">Online</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="UTR Number"
            fullWidth
            value={payData.utr_number}
            onChange={(e) => setPayData((f) => ({ ...f, utr_number: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenPayDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePaySave}
            startIcon={<FaCheckCircle />}
            sx={{
              background: "linear-gradient(45deg,#2e7d32,#66bb6a)",
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Confirm Pay
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
